import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageInfo {
  breadcrumbs: BreadcrumbItem[];
  subtitle?: string;
}

interface PageInfoContextValue {
  pageInfo: PageInfo;
  setPageInfo: (info: PageInfo) => void;
}

const PageInfoContext = createContext<PageInfoContextValue>({
  pageInfo: { breadcrumbs: [] },
  setPageInfo: () => {},
});

export const PageInfoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [pageInfo, setPageInfoState] = useState<PageInfo>({
    breadcrumbs: [],
  });

  // Clear stale breadcrumbs immediately on every route change.
  // Without this, the previous page's crumbs persist through the Suspense
  // fallback gap until the new page's PageHeader useEffect fires.
  const location = useLocation();
  useEffect(() => {
    setPageInfoState({ breadcrumbs: [] });
  }, [location.pathname]);

  const setPageInfo = useCallback((info: PageInfo) => {
    setPageInfoState(info);
  }, []);

  return (
    <PageInfoContext.Provider value={{ pageInfo, setPageInfo }}>
      {children}
    </PageInfoContext.Provider>
  );
};

export const usePageInfo = () => useContext(PageInfoContext);
